import React from 'react';
import { Link } from 'react-router-dom';

import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { ExportDropdown } from '@/components/ui/filters/ExportDropdown';
import { FilterDropdown } from '@/components/ui/filters/FilterDropdown';
import { IconScout } from '@/components/ui/images/IconScout';
import Table from '@/components/ui/tables/Table';
import { Text } from '@/components/ui/text/Text';
import { colors } from '@/assets/styles/colors';
import { useTranslation } from '@/i18n';
import { PermissionGuard } from '@/components/layout';
import { useShiftCatalogList } from '@/hooks/shift/useShiftCatalogList';
import { ROUTE_DEFINITIONS } from '@/routes';


export const ShiftsTab: React.FC = () => {
  const { t: translate } = useTranslation();
  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    actions,
    onSearch,
    onEnterSearch,
    onSortChange,
    isLoading,
    isExporting,
    onExport,
    filters,
    onFilterChange,
    workingDayTypeOptions,
    workingDayOptions,
  } = useShiftCatalogList();

  return (
    <div data-testid="shifts-tab">
      <div className="mb-4">
        <PermissionGuard action="create">
          <Link to={ROUTE_DEFINITIONS.TURNO_CREAR.path}>
            <PrimaryButton iconLeft={<IconScout name="plus" />}>
              {translate('schedule:shift.actions.create')}
            </PrimaryButton>
          </Link>
        </PermissionGuard>
      </div>

      <div className="flex flex-wrap gap-4 justify-between items-start mb-4">
        <FilterDropdown
          filters={[
            {
              key: 'workingDayType',
              label: translate('schedule:shift.filters.workingDayType'),
              options: workingDayTypeOptions,
              placeholder: translate('schedule:shift.filters.all'),
            },
            {
              key: 'workingDayId',
              label: translate('schedule:shift.filters.workingDay'),
              options: workingDayOptions,
              placeholder: translate('schedule:shift.filters.all'),
            },
            {
              key: 'status',
              label: translate('schedule:shift.filters.status'),
              options: [
                { value: '', label: translate('schedule:shift.filters.all') },
                { value: 'Activo', label: translate('schedule:shift.filters.active') },
                { value: 'Inactivo', label: translate('schedule:shift.filters.inactive') },
              ],
              placeholder: translate('schedule:shift.filters.all'),
            },
          ]}
          values={filters}
          onChangeFilter={onFilterChange}
        />
        <PermissionGuard action="view" fallback="hidden">
          <ExportDropdown
            onExport={onExport}
            placement="right"
            isLoading={isExporting}
            availableFormats={['EXCEL']}
          />
        </PermissionGuard>
      </div>

      <Table
        columns={columns}
        data={rows}
        totalRows={total}
        isLoading={isLoading}
        searchable
        onSearch={onSearch}
        onEnterSearch={onEnterSearch}
        serverSideSort
        onSortChange={onSortChange}
        actions={actions}
        pagination={{
          page,
          pageSize,
          total,
          onChange: setPage,
          onPageSizeChange: setPageSize,
        }}
        data-testid="shifts-catalog-table"
      />

      {rows.some((row) => row.hasAssignments) && (
        <div className="flex items-center gap-2 mt-2" data-testid="shifts-assigned-legend">
          {/* Mismo punto azul que marca las filas bloqueadas en la tabla. */}
          <span
            className="inline-block shrink-0"
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: colors.important.main,
            }}
            aria-hidden="true"
          />
          <Text variant="small-regular" color={colors.grays.neutral66}>
            {translate('schedule:shift.columns.assignedLegend')}
          </Text>
        </div>
      )}
    </div>
  );
};

export default ShiftsTab;
